import React from "react";
import { HashRouter as Router, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { UserDataProvider } from "./context/UserDataContext";
import { RecommendationsProvider } from "./context/RecommendationsContext";
import Index from "./pages/Index";
import Questionnaire from "./pages/Questionnaire";
import ResultsFullPage from "./pages/ResultsFullPage";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { AnimatePresence } from "framer-motion";
import Layout from "./components/Layout";
import NotFound from "./pages/NotFound";
import SavedResultsPage from "./pages/SavedResultsPage";

// Create a new QueryClient with specific settings to avoid stale data
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 0, // Don't cache any data
      retry: 0, // Don't retry failed requests by default
      refetchOnMount: true,
      refetchOnReconnect: true,
    },
  },
});

// Route change handler component
const RouteChangeHandler = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Clear any route-specific caches when navigating
    if (window.caches) {
      // This is async but we don't need to await it
      window.caches.keys().then(names => {
        names.forEach(name => {
          window.caches.delete(name);
        });
      });
    }
  }, [location]);
  
  return null;
};

// The base path should be '/' for all environments to avoid routing issues
const basename = '/';

// Create the app routes component separate from the App component
const AppRoutes = () => {
  const location = useLocation();
  
  return (
    <Layout>
      <AnimatePresence mode="wait" initial={false}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<Index />} />
          <Route path="/questionnaire" element={<Questionnaire />} />
          <Route path="/results" element={<ResultsFullPage />} />
          <Route path="/results/:hardlink" element={<SavedResultsPage />} />
          <Route path="/404" element={<NotFound />} />
          {/* Wildcard route that captures ALL other paths and redirects to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
    </Layout>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <TooltipProvider>
        <UserDataProvider>
          <RecommendationsProvider>
            <Toaster />
            <Sonner />
            <Router basename={basename}>
              <RouteChangeHandler />
              <AppRoutes />
            </Router>
          </RecommendationsProvider>
        </UserDataProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
